import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  CardContent,
  Typography,
  Button,
  Menu,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

import { useAuth } from '../../contexts/AuthContext';
import QuickActions from '../../components/Dashboard/QuickActions';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { fetchOverviewAnalytics } from '../../api/analytics';
import {
  DashboardContainer,
  DashboardSection,
  DashboardCard,
  StatTileContainer,
  StatTileTitle,
  StatTileValue,
  StatTileMeta,
} from '../../components/Common/DashboardPrimitives';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, hasSystemRole } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [periodMenuAnchor, setPeriodMenuAnchor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalRevenue: 0,
      totalReservations: 0,
      activeCampaigns: 0,
      activeBrands: 0,
      revenueGrowth: 0,
      reservationGrowth: 0,
      campaignGrowth: 0,
      brandGrowth: 0,
    },
    recentActivity: [],
    activeCampaigns: [],
    chartData: [],
  });
  const [campaignStatusCounts, setCampaignStatusCounts] = useState({});
  const [manufacturingStats, setManufacturingStats] = useState({});

  useEffect(() => {
    let mounted = true;

    const loadOverview = async () => {
      try {
        setLoading(true);
        const overview = await fetchOverviewAnalytics();
        if (!mounted) return;

        const totalRevenue = overview.campaigns?.totalRevenue || 0;
        const totalReservations = overview.campaigns?.totalReservations || 0;
        const activeCampaigns = overview.campaigns?.statuses?.active || 0;
        const totalCampaigns = Object.values(overview.campaigns?.statuses || {}).reduce((sum, value) => sum + value, 0);
        const activeBrands = overview.brands?.active || 0;

        setCampaignStatusCounts(overview.campaigns?.statuses || {});
        setManufacturingStats(overview.manufacturing || {});

        setDashboardData((prev) => ({
          ...prev,
          stats: {
            ...prev.stats,
            totalRevenue,
            totalReservations,
            activeCampaigns,
            activeBrands,
          },
          // TODO: Replace placeholder arrays with dedicated endpoints for activity/campaigns/series.
        }));
      } catch (error) {
        console.error('Failed to load dashboard overview:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadOverview();

    return () => {
      mounted = false;
    };
  }, []);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setPeriodMenuAnchor(null);
    // Fetch data for selected period
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const statTiles = [
    {
      title: 'Total Revenue',
      value: `$${dashboardData.stats.totalRevenue.toLocaleString()}`,
      delta: dashboardData.stats.revenueGrowth,
    },
    {
      title: 'Total Reservations',
      value: dashboardData.stats.totalReservations.toLocaleString(),
      delta: dashboardData.stats.reservationGrowth,
    },
    {
      title: 'Active Campaigns',
      value: dashboardData.stats.activeCampaigns,
      delta: dashboardData.stats.campaignGrowth,
    },
  ];

  if (hasSystemRole('admin')) {
    statTiles.push({
      title: 'Active Brands',
      value: dashboardData.stats.activeBrands,
      delta: dashboardData.stats.brandGrowth,
    });
  }

  return (
    <DashboardContainer>
      <DashboardSection spacing={4}>

        <Box
          sx={{
            p: 0,
            overflow: 'hidden',
            background: 'transparent',
            border: 'none',
            borderRadius: 0,
            '&:hover': {
              outline: 'none',
              boxShadow: 'none',
            },
            transform: 'none',
            transition: 'none',
          }}
        >
          <Box
            sx={{
              px: 0,
              pt: { xs: 3, md: 5 },
              pb: { xs: 4, md: 6 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0,
                mb: 3,
              }}
            >
            
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Revenue Trajectory
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(242, 244, 248, 0.6)' }}>
                  Monitor trends across your drops and catch acceleration or slowdowns instantly.
                </Typography>
      
              <Button
                variant="outlined"
                onClick={(e) => setPeriodMenuAnchor(e.currentTarget)}
                endIcon={<MoreVertIcon />}
                sx={{ '&:hover': { backgroundColor: 'transparent' } }}
              >
                Last {selectedPeriod}
              </Button>
            </Box>

            <Box sx={{ height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dashboardData.chartData}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                  <XAxis
                    dataKey="date"
                    stroke="rgba(255,255,255,0.35)"
                    tickFormatter={(value) => format(new Date(value), 'MM/dd')}
                  />
                  <YAxis stroke="rgba(255,255,255,0.35)" />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#03C6FC"
                    strokeWidth={3}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {statTiles.map((tile) => (
            <StatTileContainer key={tile.title} component="div" disableRipple sx={{ flex: 1 }}>
              <Box>
                <StatTileTitle>{tile.title}</StatTileTitle>
                <StatTileValue>{tile.value}</StatTileValue>
                <StatTileMeta
                  colorVariant={
                    tile.delta > 0 ? 'up' : tile.delta < 0 ? 'down' : undefined
                  }
                >
                  {tile.delta > 0 && '+'}
                  {tile.delta}%
                </StatTileMeta>
              </Box>
            </StatTileContainer>
          ))}
        </Stack>

        <QuickActions />

        <Stack
          direction={{ xs: 'column', xl: 'row' }}
          spacing={3}
          sx={{ alignItems: { xl: 'stretch' } }}
        >
          <DashboardCard sx={{ flex: 1 }}>
            <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">Recent Activity</Typography>
                <Button variant="text" onClick={() => navigate('/analytics')}>
                  View All
                </Button>
              </Box>
              <Divider />
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {dashboardData.recentActivity.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recent activity logged yet.
                  </Typography>
                ) : (
                  dashboardData.recentActivity.map((activity) => (
                    <ListItem
                      key={activity.id}
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderRadius: '6px',
                        backgroundColor: 'rgba(255,255,255,0.02)',
                      }}
                    >
                      <ListItemText
                        primary={activity.title}
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {activity.brand} • {formatDistanceToNow(activity.timestamp)} ago
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          size="small"
                          color={getActivityColor(activity.status)}
                          variant="outlined"
                          label={activity.status}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))
                )}
              </List>
            </CardContent>
          </DashboardCard>

          <DashboardCard
            sx={{
              width: { xs: '100%', xl: 280 },
              alignSelf: { xl: 'stretch' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                flexGrow: 1,
              }}
            >
              <Typography variant="h5">Campaign Status</Typography>
              <Divider />
              <Stack spacing={1.5}>
                {Object.entries(campaignStatusCounts).map(([status, count]) => (
                  <Box
                    key={status}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: 6,
                      padding: 1.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {status.replace('_', ' ')}
                    </Typography>
                    <Chip label={count} size="small" variant="outlined" />
                  </Box>
                ))}
              </Stack>
              <Divider />
              <Typography variant="caption" color="text.secondary">
                Manufacturing Queue: {manufacturingStats.totalOrders || 0} orders in flight
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/manufacturing/orders')}
              >
                Open Order Workflow
              </Button>
            </CardContent>
          </DashboardCard>
        </Stack>

        <DashboardCard>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h5">Active Campaigns</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/campaigns/new')}
              >
                New Campaign
              </Button>
            </Box>
            <Divider />
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap="wrap"
            >
              {dashboardData.activeCampaigns.map((campaign) => (
                <Box
                  key={campaign.id}
                  sx={{
                    flex: '1 1 320px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 6,
                    padding: 2.5,
                    border: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {campaign.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={campaign.status === 'ending_soon' ? 'Ending Soon' : 'Active'}
                      color={campaign.status === 'ending_soon' ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {campaign.brand}
                  </Typography>
                  <Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        borderRadius: '6px',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        overflow: 'hidden',
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: `${campaign.progress}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #03C6FC, #7B7DF8)',
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {campaign.reservations} / {campaign.target} reservations
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    View Campaign
                  </Button>
                </Box>
              ))}
              {dashboardData.activeCampaigns.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No active campaigns yet. Launch one to get started.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </DashboardCard>
      </DashboardSection>

      <Menu
        anchorEl={periodMenuAnchor}
        open={Boolean(periodMenuAnchor)}
        onClose={() => setPeriodMenuAnchor(null)}
      >
        {['7d', '30d', '90d'].map((period) => (
          <MenuItem key={period} onClick={() => handlePeriodChange(period)}>
            Last {period}
          </MenuItem>
        ))}
      </Menu>
    </DashboardContainer>
  );
};

export default DashboardPage;
